import React, { useState } from 'react';
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, Box } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Button } from '@mui/material';
import CategoryIcon from '@mui/icons-material/Category';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import { useTranslation } from 'react-i18next';
import { truncateString } from '../../../utils/truncateString';
import LoadingSpinner from '../../common/controls/LoadingSpinner';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useInventoryTransactionTypes } from '../../../hooks/inventory/useInventoryTransactionTypes';
import { useDeleteInventoryTransactionType } from '../../../hooks/inventory/useDeleteInventoryTransactionType';
import InventoryTransactionTypeForm from '../forms/InventoryTransactionTypeForm';
import DefaultTypesSeederDialog from './DefaultTypesSeederDialog';
import RequirePermission from '../../RequirePermission';
import { userHasAccess } from '../../../utils/userHasAccess';
import { SUB_MODULES } from '../../../constants/subModulesConstants';

export default function InventoryTransactionTypes() {

    const { t } = useTranslation('common');
    const { showSnackbar } = useSnackbar();

    const [open, setOpen] = React.useState(false);
    const handleClickOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
    };
    const [listTrigger, setListTrigger] = React.useState(false);
    const toggleListTrigger = () => {
        setListTrigger(prev => !prev);
    };

    const onEmptyData = () => {
        setSeederDialogOpen(true);
    };

    const { inventoryTransactionTypes, loading, setInventoryTransactionTypes } = useInventoryTransactionTypes(listTrigger, onEmptyData);

    const { remove } = useDeleteInventoryTransactionType(toggleListTrigger);

    const [selectedInventoryTransactionType, setSelectedInventoryTransactionType] = useState();

    const [formOpen, setFormOpen] = React.useState(false);
    const [formMode, setFormMode] = React.useState("create");
    const [seederDialogOpen, setSeederDialogOpen] =  React.useState(false);

    const handleAdd = () => {
        setSelectedInventoryTransactionType(null);
        setFormMode("create");
        setFormOpen(true);
    };

    const handleEdit = (inventoryTransactionType) => {
        setSelectedInventoryTransactionType(inventoryTransactionType);
        setFormMode("edit");
        setFormOpen(true);
    };

    const onSaveSuccess = () => {
        toggleListTrigger();
        setFormOpen(false);
    };

    const handleDelete = async (inventoryTransactionType) => {
        try {
            await remove(inventoryTransactionType.id);
            showSnackbar(t('inventory.transaction_types.messages.delete_success', { name: inventoryTransactionType.name }), "success");
        } catch (err) {
            if (err.status === 409)
            {
                showSnackbar(t('inventory.transaction_types.messages.delete_fail_still_assigned', { name: inventoryTransactionType.name }), "error");
            } else {
                showSnackbar(t('inventory.transaction_types.messages.delete_fail', { name: inventoryTransactionType.name }), "error");
            }
        }
    };

    return (
        <>
        <ListItem>
            <ListItemButton onClick={handleClickOpen}>
            <ListItemIcon>
                <CategoryIcon fontSize='large' color='secondary' />
            </ListItemIcon>
            <ListItemText 
                primary="Transaction Types"
                secondary="view and manage inventory transaction types"
            />
            </ListItemButton>
        </ListItem>

        <Dialog onClose={handleClose} open={open} disableRestoreFocus>
            <DialogTitle>
                Inventory Transaction Types
            </DialogTitle>
            <DialogContent>
                <List>
                {loading ? (
                    <ListItem>
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        <LoadingSpinner />
                    </Box>
                    </ListItem>
                ) : (
                    inventoryTransactionTypes?.map((item) => (
                    <ListItem
                        key={item.id}
                        secondaryAction={
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            {item.custom ? 
                            <VerticalHamburgerMenu
                            options={[
                                { 
                                id: 1, 
                                name: 'Edit',
                                callback: () => handleEdit(item),
                                disabled: !userHasAccess(SUB_MODULES.TRANSACTION_TYPES, 2)
                                },
                                {
                                id: 2,
                                name: 'Delete',
                                callback: () => handleDelete(item),
                                confirmMessage: t('inventory.transaction_types.messages.delete_confirm', { name: item.name }),
                                disabled: !userHasAccess(SUB_MODULES.TRANSACTION_TYPES, 3)
                                },
                            ]}
                            />
                            : null }
                        </Box>
                        }
                    >
                      <ListItemText
                          primary={`${item.name} ${item.custom ? "(custom)" : ""}`}
                          secondary={
                          <Typography variant="body2" sx={{ maxWidth: '90%' }}>
                              {truncateString(item.description, 200)}
                          </Typography>
                          }
                      />
                    </ListItem>
                    ))
                )}
                </List>
            </DialogContent>
            <DialogActions>
              <RequirePermission subModuleName={SUB_MODULES.TRANSACTION_TYPES} minAccessLevel={2}>
                <Button onClick={handleAdd}>Add Custom Type</Button>
              </RequirePermission>
              <div style={{flex: '1 0 0'}} />
              <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
        <InventoryTransactionTypeForm 
            successCallback={onSaveSuccess}
            inventoryTransactionType={selectedInventoryTransactionType}
            mode={formMode}
            open={formOpen}
            onClose={() => setFormOpen(false)}
        />
        <DefaultTypesSeederDialog
            open={seederDialogOpen}
            setOpen={setSeederDialogOpen} 
            successCallback={toggleListTrigger}
        />
        </>
    );
}